(() => {
  const SELECTORS = {
    root: "[data-asnet-addons]",
    supportRadio: 'input[name="addon_support"]',
    addonCheckbox: ".asnet-addon-checkbox",
    subtotalDisplay: "[data-subtotal-display]",
    vinInput: 'input[name="properties[VIN]"]',
  };

  function findAddonsRootForForm(formEl) {
    const productInfo = formEl.closest("product-info") || document;
    return productInfo.querySelector(SELECTORS.root);
  }

  function getSelectedSupportVariantId(rootEl) {
    const checked = rootEl.querySelector(`${SELECTORS.supportRadio}:checked`);
    return checked ? checked.value : null;
  }

  function getSelectedAddonVariantIds(rootEl) {
    return Array.from(
      rootEl.querySelectorAll(`${SELECTORS.addonCheckbox}:checked`)
    ).map((el) => el.value);
  }

  function getMainVariantIdFromForm(formEl) {
    const idInput = formEl.querySelector('input[name="id"]');
    return idInput ? idInput.value : null;
  }

  function getQuantityFromForm(formEl) {
    const qtyInput = formEl.querySelector('input[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  // Get price from radio/checkbox label
  function getPriceFromElement(element) {
    const label = element.closest("label");
    if (!label) return 0;

    const priceSpan = label.querySelector(".asnet-addons__price");
    if (!priceSpan) return 0;

    const priceText = priceSpan.textContent.trim();

    // Keep digits, commas, and periods; remove currency symbols/spaces/etc.
    let cleanPrice = priceText.replace(/[^0-9.,]/g, "");

    // If comma looks like decimal separator (ends with ,dd), convert EU to US format
    if (/,\d{2}$/.test(cleanPrice)) {
      cleanPrice = cleanPrice.replace(/\./g, "").replace(",", ".");
    } else {
      cleanPrice = cleanPrice.replace(/,/g, "");
    }

    return parseFloat(cleanPrice) || 0;
  }

  // Get main product variant price from DOM
  function getMainVariantPrice(formEl) {
    const priceElement =
      formEl.closest("product-info")?.querySelector(".price-item--regular") ||
      formEl.querySelector("[data-price]") ||
      document.querySelector(".price__regular .price-item--regular");

    if (!priceElement) return 0;

    const priceText = priceElement.textContent.trim();

    let cleanPrice = priceText.replace(/[^0-9.,]/g, "");
    if (/,\d{2}$/.test(cleanPrice)) {
      cleanPrice = cleanPrice.replace(/\./g, "").replace(",", ".");
    } else {
      cleanPrice = cleanPrice.replace(/,/g, "");
    }

    return parseFloat(cleanPrice) || 0;
  }

  // Format money (expects cents)
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || "${{amount}}"
      );
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  // VIN helpers
  function isVinRequired(rootEl) {
    // Preferred: <div data-asnet-addons data-vin-required="true">
    const attr = rootEl.getAttribute("data-vin-required");
    if (attr) return attr === "true";

    // Fallback: VIN input exists and is required
    const vinEl =
      rootEl.querySelector(SELECTORS.vinInput) ||
      document.querySelector(SELECTORS.vinInput);

    return !!(vinEl && vinEl.required);
  }

  function getVinValue(formEl, rootEl) {
    const vinEl =
      formEl.querySelector(SELECTORS.vinInput) ||
      rootEl.querySelector(SELECTORS.vinInput) ||
      document.querySelector(SELECTORS.vinInput);

    if (!vinEl) return null;

    const v = (vinEl.value || "").trim().toUpperCase();
    return v.length ? v : null;
  }

  function isValidVin(vin) {
    // 17 chars, letters/numbers, excluding I O Q
    return /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
  }

  // Update the subtotal display (display only; cart add uses /cart/add.js)
  function updateSubtotal(rootEl, formEl) {
    const subtotalDisplay = rootEl.querySelector(SELECTORS.subtotalDisplay);
    if (!subtotalDisplay) return;

    // Main price * qty
    const mainPrice = getMainVariantPrice(formEl);
    const qty = getQuantityFromForm(formEl);
    let subtotal = mainPrice * qty;

    // Support package (qty 1)
    const supportRadio = rootEl.querySelector(`${SELECTORS.supportRadio}:checked`);
    if (supportRadio) subtotal += getPriceFromElement(supportRadio);

    // Add-ons (qty 1 each)
    const checkedAddons = rootEl.querySelectorAll(`${SELECTORS.addonCheckbox}:checked`);
    checkedAddons.forEach((checkbox) => {
      subtotal += getPriceFromElement(checkbox);
    });

    // Convert dollars -> cents for formatter
    subtotalDisplay.textContent = formatMoney(Math.round(subtotal * 100));
  }

  function setupSubtotalListeners(rootEl, formEl) {
    rootEl.querySelectorAll(SELECTORS.supportRadio).forEach((radio) => {
      radio.addEventListener("change", () => updateSubtotal(rootEl, formEl));
    });

    rootEl.querySelectorAll(SELECTORS.addonCheckbox).forEach((checkbox) => {
      checkbox.addEventListener("change", () => updateSubtotal(rootEl, formEl));
    });

    if (formEl) {
      formEl.addEventListener("variant:change", () => {
        setTimeout(() => updateSubtotal(rootEl, formEl), 50);
      });

      const variantSelect = formEl.querySelector('select[name="id"], input[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener("change", () => {
          setTimeout(() => updateSubtotal(rootEl, formEl), 50);
        });
      }
    }

    const qtyInput = formEl?.querySelector('input[name="quantity"]');
    if (qtyInput) {
      qtyInput.addEventListener("change", () => updateSubtotal(rootEl, formEl));
      qtyInput.addEventListener("input", () => updateSubtotal(rootEl, formEl));
    }

    updateSubtotal(rootEl, formEl);
  }

  async function addItemsToCart(items) {
    const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.description || data?.message || "Could not add items to cart.");
    }
    return data;
  }

  function setButtonsLoading(formEl, isLoading) {
    const addButton = formEl.querySelector('[name="add"]');
    if (!addButton) return;

    if (isLoading) {
      addButton.setAttribute("aria-busy", "true");
      addButton.disabled = true;
      addButton.classList.add("loading");
    } else {
      addButton.removeAttribute("aria-busy");
      addButton.disabled = false;
      addButton.classList.remove("loading");
    }
  }

  function showInlineError(message) {
    alert(message);
  }

  async function handleSubmit(evt) {
    const formEl = evt.target;
    if (!(formEl instanceof HTMLFormElement)) return;
    if (!formEl.matches('form[action*="/cart/add"]')) return;

    const rootEl = findAddonsRootForForm(formEl);
    if (!rootEl) return;

    const mainVariantId = getMainVariantIdFromForm(formEl);
    if (!mainVariantId) return;

    const hasSupportRadios = !!rootEl.querySelector(SELECTORS.supportRadio);
    const supportId = getSelectedSupportVariantId(rootEl);
    const addonIds = getSelectedAddonVariantIds(rootEl);
    const hasAnyAddonsSelected = addonIds.length > 0;

    const vinRequired = isVinRequired(rootEl);
    const vin = getVinValue(formEl, rootEl);

    // If this product doesn't need support/addons AND no VIN requirement,
    // allow theme's normal submit behavior.
    if (!hasSupportRadios && !hasAnyAddonsSelected && !vinRequired) return;

    // Enforce support package selection if radios exist
    if (hasSupportRadios && !supportId) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      showInlineError("Please choose a Support Package.");
      return;
    }

    // Enforce VIN if required
    if (vinRequired) {
      if (!vin) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        showInlineError("Please enter your 17-character VIN.");
        return;
      }
      if (!isValidVin(vin)) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        showInlineError("VIN must be 17 characters (letters/numbers, excluding I, O, Q).");
        return;
      }
    }

    // We handle the add via AJAX, so prevent default
    evt.preventDefault();
    evt.stopImmediatePropagation();

    const qty = getQuantityFromForm(formEl);

    // Attach VIN as line item properties on the MAIN product line
    const mainLine = {
      id: mainVariantId,
      quantity: qty,
      ...(vin ? { properties: { VIN: vin } } : {}),
    };

    const items = [mainLine];
    if (supportId) items.push({ id: supportId, quantity: 1 });
    addonIds.forEach((id) => items.push({ id, quantity: 1 }));

    try {
      setButtonsLoading(formEl, true);
      await addItemsToCart(items);
      window.location.href = `${window.Shopify?.routes?.root || "/"}cart`;
    } catch (err) {
      showInlineError(err?.message || String(err));
    } finally {
      setButtonsLoading(formEl, false);
    }
  }

  function init() {
    document.addEventListener("submit", handleSubmit, true);

    document.querySelectorAll(SELECTORS.root).forEach((rootEl) => {
      const formEl =
        rootEl.closest("product-info")?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');

      if (formEl) {
        setupSubtotalListeners(rootEl, formEl);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
